import type { Session } from '../lib/session';
import { useSync } from '../lib/useSync';
import { dismissProblems, drain } from '../lib/sync';

/**
 * What the tablet has not yet told the Worker, and what the Worker refused.
 *
 * Three states, one strip under the header:
 *   no signal        amber — the day can still be entered; n changes waiting
 *   signal, queue    amber — sending, or a Send now button if it stalled
 *   refusals         red   — which change, and the Worker's reason, until dismissed
 */
export default function SyncBanner({ session }: { session: Session }) {
  const { online, queued, draining, problems } = useSync();
  const offline = session.offline || !online;
  const changes = `${queued} ${queued === 1 ? 'change' : 'changes'}`;

  return (
    <>
      {offline ? (
        <div className="banner b-warn" style={{ margin: 0, borderRadius: 0, justifyContent: 'center' }}>
          {queued > 0
            ? `No signal. The day can still be entered — ${changes} will be sent when the connection comes back.`
            : 'No signal. The day can still be entered — it will be sent when the connection comes back.'}
        </div>
      ) : queued > 0 ? (
        <div className="banner b-warn" style={{ margin: 0, borderRadius: 0, justifyContent: 'space-between' }}>
          <span>{draining ? `Sending ${changes}…` : `${changes} waiting to be sent.`}</span>
          {!draining && (
            <button type="button" className="btn sm" onClick={() => void drain()}>
              Send now
            </button>
          )}
        </div>
      ) : null}

      {problems.length > 0 && (
        <div className="banner b-no" style={{ margin: 0, borderRadius: 0, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>
              {problems.length === 1
                ? 'One change could not be applied.'
                : `${problems.length} changes could not be applied.`}
            </div>
            {problems.map((problem) => (
              <div key={problem.id} className="sub" style={{ color: 'inherit', marginTop: 3 }}>
                {problem.label} — {problem.message}
              </div>
            ))}
          </div>
          <button type="button" className="btn sm" onClick={() => void dismissProblems()}>
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}
